'use client';

import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { ChevronDown, ChevronUp } from '@components/common/Forms/Combobox/IconsCombobox';

const SALARY_MIN_STEP_BASE = 305;
const SALARY_STEP = 100;

const normalizeSalaryValue = (value: string): string => value
  .replace(/[–—−]/g, '-')
  .replace(/[^\d\s-]/g, '')
  .replace(/\s*-\s*/g, ' - ')
  .replace(/\s{2,}/g, ' ')
  .trimStart();

const getSteppedSalaryValue = (currentValue: string, direction: 1 | -1): string => {
  const normalized = normalizeSalaryValue(currentValue);
  const parts = normalized.split('-').map((p) => p.trim()).filter(Boolean);
  const first = Number.parseInt(parts[0] || '', 10);
  const second = Number.parseInt(parts[1] || '', 10);

  if (parts.length === 2 && Number.isFinite(first) && Number.isFinite(second)) {
    const span = Math.max(0, second - first);
    const steppedMin = Math.max(SALARY_MIN_STEP_BASE, first + direction * SALARY_STEP);
    return `${steppedMin} - ${Math.max(steppedMin, steppedMin + span)}`;
  }

  const base = Number.isFinite(first) ? first : SALARY_MIN_STEP_BASE;
  return String(Math.max(SALARY_MIN_STEP_BASE, base + direction * SALARY_STEP));
};

const parseSalaryParts = (value: string): { salaryMin: number; salaryMax: number } => {
  const normalized = normalizeSalaryValue(value);
  const parts = normalized.split('-').map((p) => p.trim()).filter(Boolean);
  const first = Number.parseInt(parts[0] || '', 10);
  const second = Number.parseInt(parts[1] || '', 10);

  if (parts.length === 2 && Number.isFinite(first) && Number.isFinite(second)) {
    return { salaryMin: first, salaryMax: second };
  }
  const single = Number.isFinite(first) ? first : 0;
  return { salaryMin: single, salaryMax: single };
};

interface CandidateJobSearchSalaryProps {
  onChange: (salaryMin: number, salaryMax: number) => void;
  initialSalaryMin?: number;
  initialSalaryMax?: number;
}

/**
 * @description Salary input (net, EUR) for the candidate job search form.
 * Accepts a single value ("1500") or a range ("1500 - 2000").
 * Includes up/down steppers to nudge by 100 EUR.
 */
export const CandidateJobSearchSalary = ({ onChange, initialSalaryMin, initialSalaryMax }: CandidateJobSearchSalaryProps) => {
  const { t } = useTranslation('common');
  const inputRef = useRef<HTMLInputElement>(null);

  let initialDisplay = '';
  if (initialSalaryMin) {
    initialDisplay = initialSalaryMax && initialSalaryMax !== initialSalaryMin
      ? `${initialSalaryMin} - ${initialSalaryMax}`
      : String(initialSalaryMin);
  }
  const [displayValue, setDisplayValue] = useState(initialDisplay);

  const notify = useCallback((value: string) => {
    const { salaryMin, salaryMax } = parseSalaryParts(value);
    onChange(salaryMin, salaryMax);
  }, [onChange]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const normalized = normalizeSalaryValue(e.target.value);
    setDisplayValue(normalized);
    notify(normalized);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const normalized = normalizeSalaryValue(e.target.value);
    if (normalized !== e.target.value) setDisplayValue(normalized);
  };

  const applyStep = (direction: 1 | -1) => {
    if (!inputRef.current) return;
    const next = getSteppedSalaryValue(inputRef.current.value, direction);
    setDisplayValue(next);
    notify(next);
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={t('job.add.salary.placeholder')}
        pattern="^\d+(\s?[-–—−]\s?\d+)?$"
        className="h-12 w-full rounded-2xl border border-input/65 bg-surface px-4 pr-28 text-sm font-medium text-ink placeholder:text-ink-light focus:border-secondary focus:outline-none focus:ring-2 focus:ring-secondary/20"
      />

      <div className="absolute right-3 top-0 flex h-12 items-center gap-1.5 text-ink-medium">
        <span className="flex h-7 w-5 flex-col items-center justify-center rounded-md bg-surface-50/90">
          <button
            type="button"
            tabIndex={-1}
            aria-label="Increase salary"
            className="inline-flex h-1/2 w-full items-center justify-center transition-colors hover:text-ink"
            onClick={() => applyStep(1)}
          >
            <ChevronUp className="pointer-events-none size-3.5" />
          </button>
          <button
            type="button"
            tabIndex={-1}
            aria-label="Decrease salary"
            className="inline-flex h-1/2 w-full items-center justify-center transition-colors hover:text-ink"
            onClick={() => applyStep(-1)}
          >
            <ChevronDown className="pointer-events-none size-3.5" />
          </button>
        </span>
        <span className="text-sm font-semibold tracking-tight">EUR</span>
      </div>
    </div>
  );
};
